import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    TrendingUp, Megaphone, Star, Tv, Smartphone, Zap, Eye,
    Building2, ChevronRight, ChevronLeft, Globe, BarChart3
} from 'lucide-react';
import { Hospital } from '../types';
import AdManager from './AdManager';
import AdAnalyticsDashboard from './AdAnalyticsDashboard';
import SatisfactionDashboard from './SatisfactionDashboard';
import { AdCalendar } from './AdCalendar';

type SubTab = 'overview' | 'calendar' | 'metrics' | 'satisfaction';

interface ExecutiveMarketingProps {
    theme: 'dark' | 'light';
    hospitals: Hospital[];
    groupId?: string;
    groupName?: string;
}

// TODO: Mini metas 2-9 van aquí

const ExecutiveMarketing: React.FC<ExecutiveMarketingProps> = ({
    theme, hospitals, groupId, groupName
}) => {
    // ── Mini meta 2: Theme tokens ──
    const t = useMemo(() => ({
        textWhite: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
        cardBg: theme === 'dark' ? 'bg-white/[0.02] backdrop-blur-2xl' : 'bg-white/70 backdrop-blur-2xl',
        cardBorder: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
        segmentBg: theme === 'dark' ? 'bg-white/[0.04]' : 'bg-slate-100/80',
        segmentActiveBg: theme === 'dark'
            ? 'bg-gradient-to-r from-indigo-600 to-violet-600 shadow-[0_0_20px_rgba(99,102,241,0.35)]'
            : 'bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)]',
        segmentActiveText: theme === 'dark' ? 'text-white' : 'text-slate-900',
        segmentInactiveText: theme === 'dark' ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700',
        kpiBg: theme === 'dark' ? 'bg-white/[0.03]' : 'bg-white',
    }), [theme]);

    // ── Mini meta 3: State ──
    const [activeSubTab, setActiveSubTab] = useState<SubTab>('overview');
    const [selectedHospitalId, setSelectedHospitalId] = useState<string | null>(null);

    // ── Mini meta 4: KPI calculations ──
    const kpis = useMemo(() => {
        let totalAds = 0;
        let totalActive = 0;
        let hospitalsWithAdsOn = 0;

        hospitals.forEach(h => {
            const assets = h.preferences?.adAssets || [];
            const adsEnabled = h.preferences?.adSettings?.adsEnabled ?? false;
            const activeAds = assets.filter((a: any) => a.isActive).length;

            totalAds += assets.length;
            totalActive += activeAds;
            if (adsEnabled) hospitalsWithAdsOn++;
        });

        const coverage = hospitals.length > 0
            ? Math.round((hospitalsWithAdsOn / hospitals.length) * 100)
            : 0;

        return { totalAds, totalActive, hospitalsWithAdsOn, coverage };
    }, [hospitals]);

    const selectedHospital = hospitals.find(h => h.id === selectedHospitalId) || null;

    // ── Mini meta 5-9: Render ──
    const subTabs = [
        { id: 'overview' as SubTab, label: 'Overview', icon: Megaphone },
        { id: 'calendar' as SubTab, label: 'Calendario', icon: Globe },
        { id: 'metrics' as SubTab, label: 'Métricas', icon: TrendingUp },
        { id: 'satisfaction' as SubTab, label: 'Satisfacción', icon: Star },
    ];

    return (
        <div className="space-y-6">
            {/* ── Header + Segmented Control ── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${theme === 'dark' ? 'bg-gradient-to-br from-violet-500/20 to-indigo-500/20 border border-violet-500/20' : 'bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200'} shadow-[0_0_15px_rgba(139,92,246,0.15)]`}>
                        <TrendingUp className="w-6 h-6 text-violet-500" />
                    </div>
                    <div>
                        <h2 className={`text-xl font-black ${t.textWhite} leading-tight tracking-tight`}>
                            Marketing & Reputación
                        </h2>
                        <p className={`text-xs ${t.textMuted} font-medium`}>
                            {groupName ? `Cadena: ${groupName}` : 'Panorama ejecutivo de publicidad'}
                        </p>
                    </div>
                </div>

                <div className={`${t.segmentBg} p-1 rounded-2xl border ${t.cardBorder} flex gap-1 self-start sm:self-center`}>
                    {subTabs.map((tab) => {
                        const isActive = activeSubTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveSubTab(tab.id)}
                                className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${
                                    isActive ? `${t.segmentActiveBg} ${t.segmentActiveText}` : `${t.segmentInactiveText}`
                                }`}
                            >
                                <tab.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? '' : 'opacity-70'}`} />
                                <span className="whitespace-nowrap">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Tab Content ── */}
            <AnimatePresence mode="wait">
                {activeSubTab === 'overview' && (
                    <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                        {/* ── Mini meta 6: KPI Row ── */}
                        <div className="grid grid-cols-3 gap-4 mb-8">
                            <div className={`${t.kpiBg} border ${t.cardBorder} rounded-2xl p-5 flex items-center gap-4`}>
                                <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
                                    <Zap className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Anuncios Activos</p>
                                    <p className={`text-2xl font-black ${t.textWhite} font-mono`}>{kpis.totalActive} <span className={`text-sm ${t.textMuted} font-medium`}>/ {kpis.totalAds}</span></p>
                                </div>
                            </div>
                            <div className={`${t.kpiBg} border ${t.cardBorder} rounded-2xl p-5 flex items-center gap-4`}>
                                <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`}>
                                    <Building2 className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Hospitales con Ads ON</p>
                                    <p className={`text-2xl font-black ${t.textWhite} font-mono`}>{kpis.hospitalsWithAdsOn} <span className={`text-sm ${t.textMuted} font-medium`}>/ {hospitals.length}</span></p>
                                </div>
                            </div>
                            <div className={`${t.kpiBg} border ${t.cardBorder} rounded-2xl p-5 flex items-center gap-4`}>
                                <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/30' : 'bg-indigo-50 text-indigo-600 border border-indigo-200'}`}>
                                    <BarChart3 className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Cobertura</p>
                                    <p className={`text-2xl font-black ${t.textWhite} font-mono`}>{kpis.coverage}%</p>
                                </div>
                            </div>
                        </div>

                        {/* ── Mini meta 7: Hospital Cards Grid ── */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                            {hospitals.map(h => {
                                const assets = h.preferences?.adAssets || [];
                                const adsEnabled = h.preferences?.adSettings?.adsEnabled ?? false;
                                const activeCount = assets.filter((a: any) => a.isActive).length;
                                const isSelected = selectedHospitalId === h.id;

                                return (
                                    <button
                                        key={h.id}
                                        onClick={() => setSelectedHospitalId(isSelected ? null : h.id)}
                                        className={`${t.cardBg} border rounded-2xl p-5 text-left transition-all duration-300 group ${
                                            isSelected
                                                ? 'border-indigo-500/50 shadow-[0_0_20px_rgba(99,102,241,0.15)] scale-[1.02]'
                                                : `${t.cardBorder} hover:border-indigo-500/20 hover:shadow-lg`
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className={`w-10 h-10 rounded-xl bg-white border ${t.cardBorder} flex items-center justify-center p-1.5 shadow-sm overflow-hidden`}>
                                                {h.branding?.logoUrl ? (
                                                    <img src={h.branding.logoUrl} className="w-full h-full object-contain" alt="" />
                                                ) : (
                                                    <Building2 className="w-5 h-5 text-slate-400" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className={`text-sm font-bold ${t.textWhite} truncate`}>{h.name}</h3>
                                                <p className={`text-[10px] ${t.textMuted} font-mono`}>/{h.handle}</p>
                                            </div>
                                            <ChevronRight className={`w-4 h-4 ${t.textMuted} transition-transform ${isSelected ? 'rotate-90 text-indigo-500' : 'group-hover:translate-x-1'}`} />
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                                                adsEnabled
                                                    ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                                    : `${theme === 'dark' ? 'bg-white/5 text-slate-500 border border-white/10' : 'bg-slate-100 text-slate-400 border border-slate-200'}`
                                            }`}>
                                                {adsEnabled ? '● ON' : '○ OFF'}
                                            </span>
                                            <span className={`text-xs font-bold ${t.textMuted}`}>
                                                {activeCount} anuncio{activeCount !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* ── Mini meta 8: Drill-down AdManager ── */}
                        <AnimatePresence>
                            {selectedHospital && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-4 relative">
                                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center p-1 overflow-hidden">
                                                    {selectedHospital.branding?.logoUrl ? (
                                                        <img src={selectedHospital.branding.logoUrl} className="w-full h-full object-contain" alt="" />
                                                    ) : (
                                                        <Building2 className="w-4 h-4 text-slate-400" />
                                                    )}
                                                </div>
                                                <h3 className="text-sm font-bold text-slate-800">Publicidad de {selectedHospital.name}</h3>
                                            </div>
                                            <button
                                                onClick={() => setSelectedHospitalId(null)}
                                                className="text-xs font-bold text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
                                            >
                                                <ChevronLeft className="w-4 h-4" /> Cerrar
                                            </button>
                                        </div>
                                        <AdManager
                                            hospital={selectedHospital}
                                            onUpdate={() => {}}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}

                {activeSubTab === 'calendar' && (
                    <motion.div key="calendar" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                        {/* Hospital Selector */}
                        <div className={`${t.cardBg} border ${t.cardBorder} rounded-2xl p-4 mb-6 flex items-center gap-4`}>
                            <Building2 className={`w-5 h-5 ${t.textMuted}`} />
                            <select
                                value={selectedHospitalId || ''}
                                onChange={e => setSelectedHospitalId(e.target.value || null)}
                                className={`flex-1 text-sm ${theme === 'dark' ? 'bg-white/5 text-white border-white/10' : 'bg-white text-slate-800 border-slate-200'} border rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold`}
                            >
                                <option value="">Selecciona un hospital</option>
                                {hospitals.map(h => (
                                    <option key={h.id} value={h.id}>{h.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Calendar or Empty State */}
                        {selectedHospital ? (
                            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-2 sm:p-4">
                                <AdCalendar
                                    hospital={selectedHospital}
                                    onUpdate={() => {}}
                                />
                            </div>
                        ) : (
                            <div className={`${t.cardBg} border-2 border-dashed ${t.cardBorder} rounded-2xl p-12 text-center`}>
                                <Building2 className={`w-12 h-12 mx-auto mb-3 ${t.textMuted} opacity-30`} />
                                <p className={`text-sm font-bold ${t.textMuted}`}>Selecciona un hospital para ver su calendario de campañas</p>
                            </div>
                        )}
                    </motion.div>
                )}

                {activeSubTab === 'metrics' && (
                    <motion.div key="metrics" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                        <AdAnalyticsDashboard
                            theme={theme}
                            hospitalId={hospitals[0]?.id}
                            groupId={groupId}
                            hospitals={hospitals}
                            adAssets={hospitals.flatMap(h => h.preferences?.adAssets || [])}
                        />
                    </motion.div>
                )}

                {activeSubTab === 'satisfaction' && (
                    <motion.div key="satisfaction" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                        <SatisfactionDashboard theme={theme} hospitals={hospitals} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ExecutiveMarketing;
